import { motion } from 'framer-motion';
import { Film, Clapperboard, Building2, Camera, Lightbulb, Globe, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ServiceCard } from '@/components/Cards';
import { services } from '@/data/index';
import { ROUTE_PATHS } from '@/lib/index';
import { Link } from 'react-router-dom';
import { IMAGES } from '@/assets/images';

const iconMap = {
  Film,
  Clapperboard,
  Building2,
  Camera,
  Lightbulb,
  Globe,
};

const processSteps = [
  {
    number: '01',
    title: 'Initial Consultation',
    description: 'Discuss your project vision, requirements, and creative goals with our team.',
  },
  {
    number: '02',
    title: 'Project Planning',
    description: 'Develop comprehensive production plan, timeline, and resource allocation.',
  },
  {
    number: '03',
    title: 'Pre-Production',
    description: 'Script development, storyboarding, casting, location scouting, and crew assembly.',
  },
  {
    number: '04',
    title: 'Production',
    description: 'Execute filming with world-class equipment, crew, and studio facilities.',
  },
  {
    number: '05',
    title: 'Post-Production',
    description: 'Editing, color grading, sound design, VFX coordination, and final delivery.',
  },
  {
    number: '06',
    title: 'Distribution Support',
    description: 'Festival strategy, market positioning, and global distribution consultation.',
  },
];

const serviceCategories = [
  {
    title: 'Production Services',
    description: 'Comprehensive film and commercial production capabilities',
    services: ['Film Production', 'Studio Rental', 'Equipment Rental'],
  },
  {
    title: 'Post-Production Services',
    description: 'State-of-the-art editing, grading, and finishing',
    services: ['Post-Production Services', 'Creative Development'],
  },
  {
    title: 'Global Services',
    description: 'International collaboration and co-production support',
    services: ['Global Collaboration'],
  },
];

export default function Services() {
  return (
    <div className="min-h-screen">
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.FILM_PRODUCTION_3}
            alt="Film Production"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Our Services
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Comprehensive film production and post-production services designed for global storytelling. From concept to delivery, we bring your vision to life with world-class facilities and expertise.
            </p>
            <Link to={ROUTE_PATHS.CONTACT}>
              <Button size="lg" className="group">
                Start Your Project
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Service Categories</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Organized service offerings to meet every aspect of your production needs
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {serviceCategories.map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-2xl">{category.title}</CardTitle>
                    <CardDescription className="text-base">
                      {category.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {category.services.map((service) => (
                        <li key={service} className="flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                          <span>{service}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Complete Service Portfolio</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Detailed breakdown of our comprehensive film production and post-production capabilities
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon as keyof typeof iconMap];
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <ServiceCard service={service} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Our Production Process</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              A structured workflow from initial consultation to final delivery, ensuring excellence at every stage
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {processSteps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-6">
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-2xl font-bold text-primary">
                              {step.number}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-semibold mb-2">{step.title}</h3>
                          <p className="text-muted-foreground">{step.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <Card className="bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-primary/20">
              <CardContent className="p-12 text-center">
                <h2 className="text-4xl font-bold mb-4">Ready to Start Your Project?</h2>
                <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Let's discuss how Bibia Films can bring your vision to life. Contact us for a detailed consultation and custom pricing based on your project requirements.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to={ROUTE_PATHS.CONTACT}>
                    <Button size="lg" className="w-full sm:w-auto">
                      Request a Quote
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to={ROUTE_PATHS.FACILITIES}>
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      Explore Facilities
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
